import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Heart, 
  Share2, 
  MoreHorizontal,
  User,
  Clock,
  Tag,
  ExternalLink,
  Copy,
  TrendingUp,
  Eye,
  DollarSign
} from 'lucide-react';

const NFTDetail = () => {
  const [isLiked, setIsLiked] = useState(false);
  const [activeTab, setActiveTab] = useState('details');

  const nft = {
    id: 1,
    name: "Supreme Genesis #1",
    description: "The first NFT in the Supreme Collection, representing the genesis of digital art on Supreme Chain. This unique piece combines traditional artistic elements with cutting-edge blockchain technology.",
    image: "https://images.unsplash.com/photo-1634017839464-5c339ebe3cb4?w=800&h=800&fit=crop",
    price: "0.1",
    currency: "ETH",
    creator: {
      address: "0x1234567890123456789012345678901234567890",
      name: "SupremeArtist",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face"
    },
    owner: {
      address: "0x0C0CCc31995042e9B913B357a6eaBdBa591233E7",
      name: "SupremeCollector",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face"
    },
    stats: {
      likes: 42,
      views: 128,
      volume: "0.1 ETH"
    },
    properties: [
      { trait: "Background", value: "Cosmic", rarity: "Common" },
      { trait: "Eyes", value: "Laser", rarity: "Rare" },
      { trait: "Accessory", value: "Crown", rarity: "Legendary" },
      { trait: "Mood", value: "Supreme", rarity: "Unique" }
    ],
    history: [
      { action: "Minted", user: "SupremeArtist", price: "0.05 ETH", time: "2 days ago" },
      { action: "Sold", user: "SupremeCollector", price: "0.1 ETH", time: "1 day ago" }
    ],
    isAuction: false,
    auctionEnd: null,
    collection: "Supreme Collection"
  };

  const tabs = [
    { id: 'details', label: 'Details' },
    { id: 'properties', label: 'Properties' },
    { id: 'history', label: 'History' },
    { id: 'offers', label: 'Offers' }
  ];

  const copyAddress = (address) => {
    navigator.clipboard.writeText(address);
  };

  return (
    <div className="min-h-screen py-8">
      <motion.div
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="max-w-7xl mx-auto"
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Image Section */}
          <motion.div
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="space-y-4"
          >
            <div className="relative">
              <img
                src={nft.image}
                alt={nft.name}
                className="w-full rounded-2xl"
              />
              <div className="absolute top-4 right-4 flex gap-2">
                <button
                  onClick={() => setIsLiked(!isLiked)}
                  className={`p-3 rounded-full backdrop-blur-sm transition-all duration-200 ${
                    isLiked 
                      ? 'bg-red-500/80 text-white' 
                      : 'bg-white/20 text-white hover:bg-white/30'
                  }`}
                >
                  <Heart className={`h-5 w-5 ${isLiked ? 'fill-current' : ''}`} />
                </button>
                <button className="p-3 rounded-full bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm transition-all duration-200">
                  <Share2 className="h-5 w-5" />
                </button>
                <button className="p-3 rounded-full bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm transition-all duration-200">
                  <MoreHorizontal className="h-5 w-5" />
                </button>
              </div>
            </div>
          </motion.div>

          {/* Details Section */}
          <motion.div
            initial={{ x: 20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="space-y-6"
          >
            {/* Header */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-purple-400 text-sm font-medium">{nft.collection}</span>
                <span className="text-gray-400">•</span>
                <span className="text-gray-400 text-sm">#{nft.id}</span>
              </div>
              <h1 className="text-3xl font-bold text-white mb-4">{nft.name}</h1>
              
              <div className="flex items-center gap-4 text-sm text-gray-400 mb-6">
                <div className="flex items-center gap-1">
                  <Eye className="h-4 w-4" />
                  <span>{nft.stats.views} views</span>
                </div>
                <div className="flex items-center gap-1">
                  <Heart className="h-4 w-4" />
                  <span>{nft.stats.likes} likes</span>
                </div>
                <div className="flex items-center gap-1">
                  <TrendingUp className="h-4 w-4" />
                  <span>{nft.stats.volume} volume</span>
                </div>
              </div>
            </div>

            {/* Creator & Owner */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                <p className="text-gray-400 text-sm mb-2">Creator</p>
                <div className="flex items-center gap-3">
                  <img
                    src={nft.creator.avatar}
                    alt="Creator"
                    className="w-10 h-10 rounded-full"
                  />
                  <div>
                    <p className="text-white font-medium">{nft.creator.name}</p>
                    <button
                      onClick={() => copyAddress(nft.creator.address)}
                      className="text-gray-400 text-sm hover:text-white flex items-center gap-1"
                    >
                      {nft.creator.address.slice(0, 6)}...{nft.creator.address.slice(-4)}
                      <Copy className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              </div>

              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                <p className="text-gray-400 text-sm mb-2">Owner</p>
                <div className="flex items-center gap-3">
                  <img
                    src={nft.owner.avatar}
                    alt="Owner"
                    className="w-10 h-10 rounded-full"
                  />
                  <div>
                    <p className="text-white font-medium">{nft.owner.name}</p>
                    <button
                      onClick={() => copyAddress(nft.owner.address)}
                      className="text-gray-400 text-sm hover:text-white flex items-center gap-1"
                    >
                      {nft.owner.address.slice(0, 6)}...{nft.owner.address.slice(-4)}
                      <Copy className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Price & Actions */}
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
              <div className="mb-4">
                <p className="text-gray-400 text-sm mb-1">Current Price</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold text-white">{nft.price}</span>
                  <span className="text-xl text-gray-400">{nft.currency}</span>
                </div>
              </div>

              <div className="space-y-3">
                <button className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white py-4 rounded-xl font-semibold transition-all duration-200">
                  Buy Now
                </button>
                <div className="grid grid-cols-2 gap-3">
                  <button className="bg-white/10 hover:bg-white/20 text-white py-3 rounded-xl transition-all duration-200">
                    Make Offer
                  </button>
                  <button className="bg-white/10 hover:bg-white/20 text-white py-3 rounded-xl transition-all duration-200">
                    Add to Cart
                  </button>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
              <div className="flex border-b border-white/10">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                      activeTab === tab.id
                        ? 'text-purple-400 border-b-2 border-purple-400'
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <div className="p-6">
                {activeTab === 'details' && (
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3">Description</h3>
                    <p className="text-gray-300 leading-relaxed">{nft.description}</p>
                  </div>
                )}

                {activeTab === 'properties' && (
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-4">Properties</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {nft.properties.map((prop, index) => (
                        <div key={index} className="bg-white/5 rounded-lg p-3 border border-white/10">
                          <p className="text-gray-400 text-sm">{prop.trait}</p>
                          <p className="text-white font-medium">{prop.value}</p>
                          <p className="text-purple-400 text-xs">{prop.rarity}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {activeTab === 'history' && (
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-4">Transaction History</h3>
                    <div className="space-y-3">
                      {nft.history.map((item, index) => (
                        <div key={index} className="flex items-center justify-between py-3 border-b border-white/10 last:border-b-0">
                          <div>
                            <p className="text-white font-medium">{item.action}</p>
                            <p className="text-gray-400 text-sm">by {item.user}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-white font-medium">{item.price}</p>
                            <p className="text-gray-400 text-sm">{item.time}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {activeTab === 'offers' && (
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-4">Current Offers</h3>
                    <div className="text-center py-8">
                      <DollarSign className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-400">No offers yet</p>
                      <p className="text-gray-500 text-sm">Be the first to make an offer</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default NFTDetail;
